import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

type SearchItem = { group: string; label: string; href: string; keywords?: string };

const CATALOG: SearchItem[] = [
  { group: 'Atalhos', label: 'Início', href: '/dashboard', keywords: 'dashboard home' },
  { group: 'Atalhos', label: 'Novo lançamento', href: '/financeiro/fluxo-de-caixa/novo', keywords: 'fluxo caixa' },
  { group: 'Atalhos', label: 'Nova conta a pagar', href: '/financeiro/contas/pagar/nova', keywords: 'pagar' },
  { group: 'Atalhos', label: 'Nova conta a receber', href: '/financeiro/contas/receber/nova', keywords: 'receber' },
  { group: 'Financeiro', label: 'Fluxo de caixa', href: '/financeiro/fluxo-de-caixa', keywords: 'entradas saidas' },
  { group: 'Financeiro', label: 'Contas a pagar', href: '/financeiro/contas?tab=pagar', keywords: 'pagar saldo' },
  { group: 'Financeiro', label: 'Contas a receber', href: '/financeiro/contas?tab=receber', keywords: 'receber saldo' },
  { group: 'Financeiro', label: 'Dívidas', href: '/financeiro/dividas', keywords: 'emprestimo financiamento' },
  { group: 'Patrimônio', label: 'Bens e ativos', href: '/assets', keywords: 'patrimonio' },
  { group: 'Patrimônio', label: 'Frota', href: '/patrimonio/frota', keywords: 'veiculos km' },
  { group: 'Patrimônio', label: 'Estoque p/ venda', href: '/vehicles', keywords: 'estoque' },
  { group: 'Patrimônio', label: 'Aluguéis', href: '/rentals', keywords: 'aluguel locacao' },
  { group: 'Patrimônio', label: 'Embarcações', href: '/vessels', keywords: 'barcos naves' },
  { group: 'Patrimônio', label: 'Aeronaves', href: '/aircrafts', keywords: 'avioes' },
  { group: 'Patrimônio', label: 'Investimentos', href: '/investments', keywords: 'carteira' },
  { group: 'Patrimônio', label: 'Filantropia', href: '/philanthropies', keywords: 'doacao' },
  { group: 'Patrimônio', label: 'Empresas', href: '/companies', keywords: 'empresa holding' },
  { group: 'Relatórios', label: 'Resumos', href: '/new-reports', keywords: 'relatorios' },
  { group: 'Config', label: 'Preferências / Segurança', href: '/settings/security', keywords: 'config segurança mfa autenticação' },
  { group: 'Config', label: 'Identidade visual', href: '/whitelabel', keywords: 'config marca identidade whitelabel' },
];

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return CATALOG.slice(0, 8);
    return CATALOG.filter(
      i =>
        i.label.toLowerCase().includes(term) ||
        i.group.toLowerCase().includes(term) ||
        (i.keywords || '').includes(term)
    ).slice(0, 12);
  }, [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => setActive(0), [q, open]);

  const go = (href: string) => {
    setOpen(false);
    setQ('');
    navigate(href);
  };

  const grouped = results.reduce<Record<string, SearchItem[]>>((acc, item) => {
    (acc[item.group] ||= []).push(item);
    return acc;
  }, {});

  return (
    <div className="global-search-wrap" ref={wrapRef}>
      <button
        type="button"
        className="global-search"
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        aria-label="Busca global"
      >
        <MagnifyingGlassIcon style={{ width: 16, height: 16 }} />
        <strong>Buscar…</strong>
        <kbd>⌘K</kbd>
      </button>
      {open && (
        <div className="global-search-panel" role="listbox">
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--slate-100)' }}>
            <input
              ref={inputRef}
              className="input search"
              style={{ width: '100%', minWidth: 0 }}
              placeholder="Contas, fluxo, atalhos…"
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActive(i => Math.min(i + 1, results.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActive(i => Math.max(i - 1, 0));
                } else if (e.key === 'Enter' && results[active]) {
                  e.preventDefault();
                  go(results[active].href);
                }
              }}
            />
          </div>
          {results.length === 0 ? (
            <div className="muted" style={{ padding: 16 }}>Nenhum resultado.</div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group}>
                <div className="gs-group">{group}</div>
                {items.map(item => {
                  const idx = results.indexOf(item);
                  return (
                    <button
                      key={item.href + item.label}
                      type="button"
                      className={`gs-item${idx === active ? ' active' : ''}`}
                      style={{ width: '100%', border: 'none', background: idx === active ? undefined : 'transparent', textAlign: 'left' }}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => go(item.href)}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
